import Button from "@imminently/decisively-ui/Button";
import Dialog from "@imminently/decisively-ui/Dialog";
import TextInput from "@imminently/decisively-ui/TextInput";
import type React from "react";
import { useEffect, useState } from "react";

/**
 * Rename or delete a (non-default) investigation view. Deleting asks for confirmation in place.
 * `onRename` and `onDelete` report their own errors and resolve to whether they succeeded, so the
 * dialog only closes on success.
 */
export const EditInvestigationDialog = ({
  open,
  onOpenChange,
  name,
  onRename,
  onDelete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  onRename: (name: string) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
}) => {
  const [draftName, setDraftName] = useState(name);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  // Start from the current name each time the dialog opens.
  useEffect(() => {
    if (open) {
      setDraftName(name);
      setConfirmingDelete(false);
    }
  }, [open, name]);

  const trimmed = draftName.trim();

  const run = async (action: () => Promise<boolean>) => {
    setBusy(true);
    try {
      if (await action()) onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!trimmed || busy) return;
    if (trimmed === name) {
      onOpenChange(false);
      return;
    }
    run(() => onRename(trimmed));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      intent="compact"
    >
      <Dialog.Content className="flex flex-col">
        {confirmingDelete ? (
          <>
            <Dialog.Header>
              <Dialog.Title>Delete "{name}"?</Dialog.Title>
              <Dialog.Description>
                Its cards and their saved card state will be removed. This can't be undone.
              </Dialog.Description>
              <Dialog.Controls />
            </Dialog.Header>
            <Dialog.Actions>
              <Button
                label="Back"
                disabled={busy}
                onClick={() => setConfirmingDelete(false)}
              >
                Back
              </Button>
              <Dialog.Actions.PrimaryButton
                intent="destructive"
                label="Delete view"
                loading={busy}
                onClick={() => run(onDelete)}
              >
                Delete view
              </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
          </>
        ) : (
          <form
            className="contents"
            onSubmit={handleSubmit}
          >
            <Dialog.Header>
              <Dialog.Title>Edit view</Dialog.Title>
              <Dialog.Controls />
            </Dialog.Header>
            <Dialog.Body>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="edit-investigation-name"
                  className="text-sm font-semibold"
                >
                  Name
                </label>
                <TextInput
                  id="edit-investigation-name"
                  value={draftName}
                  onValueChange={setDraftName}
                  autoFocus
                  fullWidth
                />
              </div>
            </Dialog.Body>
            <Dialog.Actions className="justify-between">
              <Button
                variant="ghost"
                intent="destructive"
                label="Delete view"
                disabled={busy}
                onClick={() => setConfirmingDelete(true)}
              >
                Delete view
              </Button>
              <div className="flex items-center gap-2">
                <Dialog.Actions.CancelButton
                  label="Cancel"
                  disabled={busy}
                />
                <Dialog.Actions.PrimaryButton
                  type="submit"
                  label="Save"
                  loading={busy}
                  disabled={!trimmed}
                >
                  Save
                </Dialog.Actions.PrimaryButton>
              </div>
            </Dialog.Actions>
          </form>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
